import {
  renderBulletButton,
  syncBulletCurrent,
} from "../helpers/paginationBullets";

class SectionSlideshow extends HTMLElement {
  constructor() {
    super();
    this.slides = this.querySelectorAll(".slideshow__item");
    this.duplicateSlidesCount = this.querySelectorAll(
      ".slideshow__item.slideshow__item--duplicate",
    ).length;
    this.ctas = this.querySelectorAll(".slideshow__cta");
    this.disableAboveBreakpoint = this.dataset.disableAboveBreakpoint;

    window.loadSwiper.then((swiperModules) => {
      this.swiperModules = swiperModules;

      if (this.disableAboveBreakpoint) {
        this.disableQuery = window.matchMedia(
          `(min-width: ${window.themeBreakpoints[this.disableAboveBreakpoint]}px)`,
        );
        this.disableQuery.addEventListener("change", () => this.toggleSwiper());
      }

      this.toggleSwiper();
    });
  }

  toggleSwiper() {
    if (this.disableQuery?.matches) {
      this.swiper?.destroy(true, true);
      this.swiper = null;
      this.clearInert();
    } else if (!this.swiper) {
      this.initSwiper();
    }
  }

  initSwiper() {
    const { Swiper, Pagination, Navigation, A11y } = this.swiperModules;
    const swiperParams = {
      modules: [Pagination, Navigation, A11y],
      loop: this.slides.length > 2,
      slidesPerView: "auto",
      centeredSlides: true,
      spaceBetween: 8,
      // Maintains `swiper-slide-visible`, which syncInert reads.
      watchSlidesProgress: true,
      a11y: {
        prevSlideMessage: this.dataset.a11yPrevious,
        nextSlideMessage: this.dataset.a11yNext,
        firstSlideMessage: this.dataset.a11yFirst,
        lastSlideMessage: this.dataset.a11yLast,
        // Slide labels are applied by labelSlides, which discounts the
        // duplicates. Off-screen slides are inert, so focus can never land in
        // one and Swiper's scroll-into-view handler has nothing to react to.
        slideLabelMessage: null,
        scrollOnFocus: false,
      },
      navigation: {
        nextEl: this.querySelector("[data-navigation-next]"),
        prevEl: this.querySelector("[data-navigation-previous]"),
      },
      breakpoints: {
        1024: {
          spaceBetween: 24,
        },
      },
      pagination: {
        el: this.querySelector(".swiper-pagination"),
        clickable: true,
        renderBullet: renderBulletButton,
      },
      on: {
        init: (swiper) => {
          this.updateNavPosition(swiper);
          this.updateActiveCta(swiper);
        },
        afterInit: (swiper) => {
          this.labelSlides(swiper);
          this.syncInert(swiper);
        },
        slideChange: (swiper) => {
          this.updateNavPosition(swiper);
        },
        resize: (swiper) => {
          this.updateNavPosition(swiper);
          this.syncInert(swiper);
        },
        sliderFirstMove: () => {
          this.hideCtas();
        },
        slideChangeTransitionStart: () => {
          this.hideCtas();
        },
        transitionEnd: (swiper) => {
          this.updateActiveCta(swiper);
        },
        // Driven off the translate rather than the transition events, which do
        // not arrive for a drag, a cancelled animation or a 0ms move and would
        // strand `inert` on the wrong slides.
        setTranslate: (swiper) => {
          this.syncInert(swiper);
        },
        breakpoint: (swiper) => {
          this.syncInert(swiper);
        },
        slidesLengthChange: (swiper) => {
          this.labelSlides(swiper);
          this.syncInert(swiper);
        },
        touchEnd: (swiper) => {
          // Emitted before Swiper decides whether to transition, so wait a
          // frame: a drag that settles without moving the carousel never
          // reaches transitionEnd and would leave the CTA hidden.
          requestAnimationFrame(() => {
            if (!swiper.animating) this.updateActiveCta(swiper);
          });
        },
      },
    };

    // When using uplicate slides we need to handle pagination in a special way
    // Approach pulled from https://github.com/nolimits4web/swiper/issues/7254#issuecomment-4193273635
    if (this.duplicateSlidesCount) {
      swiperParams.pagination.renderBullet = (index, className) =>
        index < this.duplicateSlidesCount
          ? renderBulletButton(index, className)
          : "";

      swiperParams.on.realIndexChange = (swiper) => {
        const bullets = swiper.pagination?.bullets;
        if (!bullets || !bullets.length) return;

        // Calculate the true active index using modulo math
        const realIndex = swiper.realIndex % this.duplicateSlidesCount;

        // Loop through the remaining bullets and toggle the active class
        bullets.forEach((bullet, i) => {
          bullet.classList.toggle(
            "swiper-pagination-bullet-active",
            i === realIndex,
          );
        });

        // Runs after Swiper's own paginationUpdate, so the A11y module has
        // already set aria-current from the classes this just replaced.
        syncBulletCurrent(swiper);
      };
    }
    this.swiper = new Swiper(this.querySelector(".swiper"), swiperParams);
  }

  /**
   * Removes off-screen slides from the tab order and the accessibility tree, so
   * keyboard and screen reader users only reach what is actually on screen.
   * `inert` covers every focusable descendant in one attribute, where
   * aria-hidden + tabindex="-1" has to enumerate them and trips the
   * aria-hidden-focus rule when it misses one.
   */
  syncInert(swiper) {
    if (!swiper || swiper.destroyed) return;

    swiper.slides.forEach((slide) => {
      // Partially visible slides are still perceivable, so they stay reachable.
      const isVisible = slide.classList.contains("swiper-slide-visible");

      // Inerting an ancestor of the focused element blurs it, dropping the user
      // back to the top of the document mid-interaction.
      if (!isVisible && slide.contains(document.activeElement)) return;

      slide.toggleAttribute("inert", !isVisible);
    });
  }

  /**
   * Swiper's own `cleanStyles` teardown knows nothing about `inert`, so without
   * this every slide stays unreachable at widths where the carousel is disabled.
   */
  clearInert() {
    this.slides.forEach((slide) => slide.removeAttribute("inert"));
  }

  /**
   * Liquid duplicates a three-slide set so Swiper has enough slides to loop
   * without gaps, which would otherwise be announced as six slides instead of
   * three. Positions are resolved against the real set, matching the pagination.
   */
  labelSlides(swiper) {
    const total = this.duplicateSlidesCount || swiper.slides.length;

    swiper.slides.forEach((slide, index) => {
      // Only present in loop mode, where Swiper reorders the slide elements.
      const slideIndex = Number(slide.dataset.swiperSlideIndex ?? index);

      slide.setAttribute(
        "aria-label",
        this.slideLabel(slideIndex % total, total),
      );
    });
  }

  slideLabel(index, total) {
    return this.dataset.a11ySlide
      .replace("{{index}}", index + 1)
      .replace("{{total}}", total);
  }

  updateActiveCta(swiperInstance) {
    if (!this.ctas.length) return;

    const activeIndex = swiperInstance.realIndex % this.ctas.length;

    this.ctas.forEach((cta, index) => {
      cta.classList.toggle("slideshow__cta--active", index === activeIndex);
    });
  }

  hideCtas() {
    this.ctas.forEach((cta) => {
      cta.classList.remove("slideshow__cta--active");
    });
  }

  updateNavPosition(swiperInstance) {
    const activeSlide = swiperInstance.slides[swiperInstance.activeIndex];
    if (!activeSlide) return;

    const imgContainer = activeSlide.querySelector(".slideshow__item-image");

    if (!imgContainer) return;

    const imgHeight = imgContainer.offsetHeight;

    swiperInstance.el.style.setProperty(
      "--current-alignment-height",
      `${imgHeight}px`,
    );
  }
}

if (!customElements.get("section-slideshow")) {
  customElements.define("section-slideshow", SectionSlideshow);
}
